import type { FC } from 'react';
import { type PropsWithChildren, useCallback } from 'react';

import { API_UI_THEME, useUserPreference } from '@dbt-labs/app-nav';
import { type Theme, ThemeProvider } from '@dbt-labs/sourdough';

export const AppThemeProvider: FC<PropsWithChildren> = ({ children }) => {
  const uiTheme = useUserPreference({
    id: 'ui_theme',
    defaultValue: API_UI_THEME.UNDETERMINED,
  });

  /**
   * if unable to reach user preferences, allow ThemeProvider to set the default theme based on local storage
   * required for localhost -> non-dev environments as user preferences are not reachable with PATs in that case
   */
  const userTheme =
    uiTheme.value === API_UI_THEME.UNDETERMINED ? undefined : uiTheme.value;

  const onUpdate = useCallback(
    (theme: Theme) => {
      if (!uiTheme.isError) {
        uiTheme.update(theme);
      }
    },
    [uiTheme],
  );

  return (
    <ThemeProvider
      theme={userTheme}
      onUpdate={onUpdate}
      isEnabled={true}
      supportsSystemPreference={userTheme === API_UI_THEME.SYSTEM}
      shouldStoreLocally={uiTheme.isError}
    >
      {children}
    </ThemeProvider>
  );
};
